// PLAN-cockpit-v2 Phase 0-C: 共通 ResultRow (Result list の 1 行)
// title / one_line / domain badges / source_project / created_at
import { permalinkHref, itemLinkClick } from '../../lib/itemLink'

interface ResultRowProps {
  id: string  // PLAN-permalink-cockpit-convergence: 行を <a href="/v/:id"> にするため必須
  title: string
  one_line?: string | null
  origin?: string | null
  subjectivity?: string | null
  source_project?: string | null
  created_at?: string | null
  declared_domains?: string[]
  inferred_domains?: { domain: string; confidence?: number | null }[]
  domains?: string[]  // PLAN-domain-classifier-iter2: 全 domain 配列 (declared > curated > inferred 順)
  visibility?: string | null  // PLAN-domain-classifier-iter2: public-* なら badge 表示
  has_decision?: number  // PLAN-cockpit-decision-visibility: decision 保有マーカー
  x_status?: string | null
  selected?: boolean
  onClick?: () => void
  highlight?: string[] // FTS/hybrid mode のみ、term をハイライト
}

// PLAN-domain-classifier-iter2 (2026-05-11): pill 全体に薄い背景色 + dot で domain 識別
function DomainPill({ domain }: { domain: string }) {
  // CSS color-mix で domain color の 18% 透過版を背景に。border は 35% 透過。
  const cssVar = `var(--domain-${domain})`
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-text-base"
      style={{
        backgroundColor: `color-mix(in srgb, ${cssVar} 16%, transparent)`,
        border: `1px solid color-mix(in srgb, ${cssVar} 35%, transparent)`,
      }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cssVar }} aria-hidden />
      {domain}
    </span>
  )
}

function highlightText(text: string, terms: string[]): React.ReactNode {
  if (!terms || terms.length === 0) return text
  const valid = terms.filter((t) => t && t.length >= 2)
  if (valid.length === 0) return text
  const re = new RegExp(`(${valid.map(escapeRegExp).join('|')})`, 'gi')
  const parts = text.split(re)
  return parts.map((p, i) =>
    valid.some((t) => p.toLowerCase() === t.toLowerCase()) ? (
      <mark key={i} className="bg-state-warning/20 text-text-base rounded px-0.5">{p}</mark>
    ) : (
      <span key={i}>{p}</span>
    )
  )
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const ms = Date.now() - new Date(iso).getTime()
    const sec = Math.floor(ms / 1000)
    if (sec < 60) return 'just now'
    if (sec < 3600) return `${Math.floor(sec / 60)}m`
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`
    if (sec < 86400 * 30) return `${Math.floor(sec / 86400)}d`
    return new Date(iso).toISOString().slice(0, 10)
  } catch {
    return iso
  }
}

// MM/DD HH:MM 形式
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    return `${mm}/${dd} ${hh}:${mi}`
  } catch {
    return iso
  }
}

export function ResultRow(props: ResultRowProps) {
  const {
    id,
    title,
    one_line,
    origin,
    subjectivity,
    source_project,
    created_at,
    domains = [],
    visibility,
    has_decision,
    x_status,
    selected,
    onClick,
    highlight = [],
  } = props
  const hasDecision = !!has_decision
  const isPublic = visibility === 'public-searchable' || visibility === 'public-unsearchable'
  // declared_domains / inferred_domains は backward-compat のため interface に残置だが未使用 (domains に統合)
  void props.declared_domains
  void props.inferred_domains

  const isArticle = origin === 'synthesized'
  const projectLabel = source_project ?? (subjectivity ?? null)

  return (
    <a
      href={permalinkHref(id)}
      onClick={(e) => itemLinkClick(e, () => onClick?.())}
      className={`
        block w-full text-left px-4 py-3 border-b border-border-subtle
        transition flex flex-col gap-1
        ${selected ? 'bg-bg-overlay' : 'hover:bg-bg-overlay'}
      `}
    >
      {/* Row 1: TYPE / project / datetime */}
      <div className="flex items-baseline gap-2 text-[11px]">
        <span className={`uppercase tracking-wide font-medium ${isArticle ? 'text-accent-secondary' : 'text-text-subtle'}`}>
          {isArticle ? 'Article' : 'Entry'}
        </span>
        {hasDecision && (
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0 ${
              x_status === 'all_rejected' ? 'text-text-subtle' : 'text-emerald-600'
            }`}
            style={{
              backgroundColor: x_status === 'all_rejected'
                ? 'color-mix(in srgb, var(--text-subtle) 14%, transparent)'
                : 'color-mix(in srgb, var(--state-success, #10b981) 16%, transparent)',
            }}
            title={`Decision (${x_status ?? 'present'})`}
          >
            ◇ Decision
          </span>
        )}
        {projectLabel && (
          <span className="text-text-subtle truncate">{projectLabel}</span>
        )}
        {isPublic && (
          <span
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--state-warning) 18%, transparent)',
              color: 'var(--state-warning)',
              border: '1px solid color-mix(in srgb, var(--state-warning) 40%, transparent)',
            }}
            title="Public — 認証なしで外部から閲覧可能"
          >
            🔓 Public
          </span>
        )}
        {domains.length > 0 && (
          <span className="inline-flex flex-wrap gap-1 flex-shrink-0">
            {domains.map(d => <DomainPill key={d} domain={d} />)}
          </span>
        )}
        {created_at && (
          <span className="ml-auto text-text-subtle font-mono flex-shrink-0">
            {formatDateTime(created_at)}
          </span>
        )}
      </div>

      {/* Row 2: Title */}
      <h3 className="text-sm font-medium text-text-base line-clamp-1">
        {highlightText(title, highlight)}
      </h3>

      {/* Row 3: Contents (2 lines) */}
      {one_line && (
        <p className="text-xs text-text-muted line-clamp-2">
          {highlightText(one_line, highlight)}
        </p>
      )}

      {/* Domain badges は Row 1 の DomainPill に統合済 (PLAN-domain-classifier-iter2、2026-05-11)
          declared_domains / inferred_domains props は backward-compat のため残置 */}
    </a>
  )
}

// 旧 relativeTime export 維持 (他で使われている可能性のため)
export { relativeTime }
